import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import axios from "axios";
import {
  adminGrowthSnapshotCreateSectionAction,
  adminGrowthSnapshotGetAction,
  adminGrowthSnapshotSectionGetAction,
  adminGrowthSnapshotUpdateSectionAction
} from "../Action/action";
import config from "../coreFIles/config";
import { getAdminAuthToken } from "../coreFIles/auth";
import CKEditorWithSource from "./CKEditorWithSource";

function buildApiUrl(endpoint) {
  const base = (config.apiUrl || "").replace(/\/+$/, "");
  const path = String(endpoint || "").replace(/^\/+/, "");
  return base ? `${base}/${path}` : `/${path}`;
}

function resolvePublicUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  let origin = "";
  try {
    if (config.apiUrl) origin = new URL(config.apiUrl).origin;
  } catch (e) {
    origin = "";
  }
  if (!origin) origin = "http://localhost:3001";
  if (s.startsWith("/")) return `${origin}${s}`;
  return `${origin}/${s}`;
}

class CkUploadAdapter {
  constructor(loader) {
    this.loader = loader;
    this.controller = new AbortController();
  }
  abort() {
    try {
      this.controller.abort();
    } catch (e) {}
  }
  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("upload", file);
    const token = getAdminAuthToken();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const resp = await axios.post(buildApiUrl("/admin/upload/image"), fd, {
      headers,
      withCredentials: true,
      signal: this.controller.signal
    });
    const url = resp?.data?.url;
    if (!url) throw new Error("Upload failed");
    return { default: resolvePublicUrl(url) };
  }
}

function CkUploadAdapterPlugin(editor) {
  // eslint-disable-next-line no-param-reassign
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => new CkUploadAdapter(loader);
}

const editorContainerStyle = { border: "1px solid var(--gray-300)", borderRadius: 6, overflow: "hidden" };
const editorConfig = { extraPlugins: [CkUploadAdapterPlugin] };

export default function GrowthSnapshotSectionForm({ mode = "create" }) {
  const isEdit = mode === "edit";
  const { snapshotId, sectionId } = useParams();
  const navigate = useNavigate();
  const listPath = `/growth-snapshots/${snapshotId}/sections`;

  const [snapshotTitle, setSnapshotTitle] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    card_title: "",
    short_description: "",
    description: "",
    display_order: "0",
    estimated_duration: "",
    phase: "both",
    status: 1
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await adminGrowthSnapshotGetAction(snapshotId);
        if (!cancelled && res?.success) setSnapshotTitle(res.data?.title || "");
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [snapshotId]);

  useEffect(() => {
    if (!isEdit || !sectionId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminGrowthSnapshotSectionGetAction(sectionId);
        if (cancelled) return;
        if (!res?.success || !res.data) {
          setError(res?.message || "Section not found.");
          return;
        }
        const d = res.data;
        setForm({
          title: d.title || "",
          subtitle: d.subtitle || "",
          card_title: d.card_title || "",
          short_description: d.short_description || "",
          description: d.description || "",
          display_order: d.display_order != null ? String(d.display_order) : "0",
          estimated_duration: d.estimated_duration || "",
          phase: d.phase === "beginning" || d.phase === "end" || d.phase === "both" ? d.phase : "both",
          status: Number(d.status) === 0 ? 0 : 1
        });
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || "Failed to load.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, sectionId]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!String(form.title || "").trim()) {
      toast.error("Title is required", { toastId: "gs-section-form" });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        subtitle: form.subtitle.trim() || null,
        card_title: form.card_title.trim() || null,
        short_description: form.short_description || "",
        description: form.description || "",
        display_order: Number(form.display_order) || 0,
        estimated_duration: form.estimated_duration || "",
        phase: form.phase || "both",
        status: Number(form.status) === 1 ? 1 : 0
      };
      const res = isEdit
        ? await adminGrowthSnapshotUpdateSectionAction(sectionId, payload)
        : await adminGrowthSnapshotCreateSectionAction(snapshotId, payload);
      if (!res?.success) throw new Error(res?.message || "Save failed");
      toast.success(isEdit ? "Section updated" : "Section created", { toastId: "gs-section-form" });
      navigate(listPath);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed", {
        toastId: "gs-section-form"
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="content">
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">{error}</div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="breadcrumb mb-0">
          <Link to="/growth-snapshots">Growth Snapshots</Link>
          <span>›</span>
          <Link to={listPath}>{snapshotTitle || "Snapshot"}</Link>
          <span>›</span>
          <span>{isEdit ? "Edit section" : "Add section"}</span>
        </div>
        <Link className="btn btn-outline btn-sm" to={listPath}>
          ← Back to list
        </Link>
      </div>

      <form className="card" onSubmit={onSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Title *</label>
            <input
              className="form-control"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              placeholder="Child Snapshot"
            />
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <input
              className="form-control"
              value={form.subtitle}
              onChange={(e) => setField("subtitle", e.target.value)}
              placeholder="Optional subtitle"
            />
          </div>
        </div>

        <div className="form-group">
          <label>Card title</label>
          <input
            className="form-control"
            value={form.card_title}
            onChange={(e) => setField("card_title", e.target.value)}
            placeholder="Title shown on the card"
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Phase</label>
            <select
              className="form-control"
              value={form.phase}
              onChange={(e) => setField("phase", e.target.value)}
            >
              <option value="beginning">Beginning only</option>
              <option value="end">End only</option>
              <option value="both">Both (beginning &amp; end)</option>
            </select>
            <p className="small-label" style={{ marginTop: 8 }}>
              Use Beginning for Welcome and End for Welcome back when Q1–Q15 are duplicated.
            </p>
          </div>
          <div className="form-group">
            <label>Estimated duration</label>
            <input
              className="form-control"
              value={form.estimated_duration}
              onChange={(e) => setField("estimated_duration", e.target.value)}
              placeholder="e.g. 5 min"
            />
          </div>
          
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Status</label>
            <select
              className="form-control"
              value={form.status}
              onChange={(e) => setField("status", Number(e.target.value))}
            >
              <option value={1}>Active</option>
              <option value={0}>Inactive</option>
            </select>
          </div>
          <div className="form-group">
            <label>Display order</label>
            <input
              className="form-control"
              type="number"
              min="0"
              value={form.display_order}
              onChange={(e) => setField("display_order", e.target.value)}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Short description</label>
          <CKEditorWithSource
            heightClassName="ckeditor-height-200"
            containerStyle={editorContainerStyle}
            config={editorConfig}
            data={form.short_description}
            onChange={(html) => setField("short_description", html)}
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <CKEditorWithSource
            heightClassName="ckeditor-height-400"
            containerStyle={editorContainerStyle}
            config={editorConfig}
            data={form.description}
            onChange={(html) => setField("description", html)}
          />
        </div>

        <div className="form-actions">
          <button className="btn btn-primary" type="submit" disabled={saving}>
            {saving ? "Saving…" : isEdit ? "Update" : "Save"}
          </button>
          <Link className="btn btn-outline" to={listPath}>
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
